import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building, Layers, Users, CalendarDays, ChevronDown, ChevronRight, Search } from "lucide-react";
import dayjs from "dayjs";
import { DatePicker, Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { EMPLOYEE_OCCUPANCY } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { EmployeeDetails } from "../../components/ui/cards";
import { DialogModal } from "../../components/ui/dialogModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toolbarInputClass, toolbarPickerClass, toolbarSelectClass } from "../../components/ui/fieldStyles";
import { masterFormSelectProps } from "../../components/common/BranchFilterDropdown";
import { BRANCH_FILTER_CHANGED_EVENT, getListBranchScopeKey } from "../../utils/branchSession";

const floorStatusClass = {
    short: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    surplus: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    ok: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    unset: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

const personDetails = (employee) => {
    const nameParts = String(employee.employee_name || "").trim().split(/\s+/);
    return {
        first_name: nameParts[0] || employee.employee_name || "-",
        last_name: nameParts.slice(1).join(" "),
        employee_id: employee.employee_id || employee.id,
        profile: employee.profile || "",
        designation_id: { designation_name: employee.designation || "" },
    };
};

const idsBetween = (ids, fromId, toId) => {
    const start = ids.indexOf(fromId);
    const end = ids.indexOf(toId);
    if (start < 0 || end < 0) return [toId];
    const [lo, hi] = start < end ? [start, end] : [end, start];
    return ids.slice(lo, hi + 1);
};

const personMatchesQuery = (employee, query) => {
    if (!query) return true;
    const hay = `${employee.employee_name || ""} ${employee.employee_id || ""} ${employee.designation || ""}`.toLowerCase();
    return hay.includes(query);
};

const OccupancyPerson = ({
    employee, canMove, selected, matched, onSelect, onDragIds, onChangePosting,
}) => {
    const temp = Boolean(employee.is_temp);
    const draggedRef = useRef(false);
    return (
        <div
            draggable={canMove}
            onDragStart={(event) => {
                if (!canMove) return;
                draggedRef.current = true;
                const ids = onDragIds(employee.id);
                event.dataTransfer.setData("application/json", JSON.stringify({
                    employee_ids: ids,
                    from_floor_id: employee.effective_floor_id || employee.work_floor_id || null,
                }));
                event.dataTransfer.effectAllowed = "move";
            }}
            onDragEnd={() => {
                window.setTimeout(() => {
                    draggedRef.current = false;
                }, 0);
            }}
            onClick={(event) => {
                if (!canMove || draggedRef.current) return;
                onSelect(employee, event);
            }}
            onDoubleClick={(event) => {
                event.preventDefault();
                if (canMove && onChangePosting && !draggedRef.current) onChangePosting(employee);
            }}
            className={cn(
                "rounded-lg border px-2 py-1.5 bg-white dark:bg-gray-800",
                temp
                    ? "border-amber-300 dark:border-amber-700"
                    : "border-gray-100 dark:border-gray-700",
                selected && "ring-2 ring-sidebar-primary",
                matched && !selected && "ring-1 ring-sidebar-primary/70 bg-sidebar-primary/5",
                canMove && "cursor-grab active:cursor-grabbing",
            )}
            title={canMove ? "Click to select, Shift-click a range, drag onto a floor. Click a posted person to change Temp/Home." : undefined}
        >
            <div className="flex items-center justify-between gap-2">
                <EmployeeDetails details={personDetails(employee)} size="xs" isFromSubordinate />
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                    {temp ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            Temp
                        </span>
                    ) : employee.effective_floor_id || employee.work_floor_id ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sidebar-primary/10 text-sidebar-primary">
                            Home
                        </span>
                    ) : null}
                    {employee.is_present ? (
                        <span className="text-[10px] text-green-600 dark:text-green-400">Present</span>
                    ) : (
                        <span className="text-[10px] text-muted-foreground">Planned</span>
                    )}
                </div>
            </div>
        </div>
    );
};

const DropZone = ({ canMove, onDropPayload, onDragEnterZone, className, children }) => {
    const [over, setOver] = useState(false);
    const depthRef = useRef(0);

    const resetHighlight = () => {
        depthRef.current = 0;
        setOver(false);
    };

    return (
        <div
            onDragEnter={(event) => {
                if (!canMove) return;
                event.preventDefault();
                event.stopPropagation();
                depthRef.current += 1;
                setOver(true);
                onDragEnterZone?.();
            }}
            onDragOver={(event) => {
                if (!canMove) return;
                event.preventDefault();
                event.stopPropagation();
            }}
            onDragLeave={(event) => {
                event.stopPropagation();
                depthRef.current -= 1;
                if (depthRef.current <= 0) resetHighlight();
            }}
            onDrop={(event) => {
                if (!canMove) return;
                event.preventDefault();
                event.stopPropagation();
                resetHighlight();
                try {
                    const payload = JSON.parse(event.dataTransfer.getData("application/json") || "{}");
                    if (payload.employee_ids?.length) onDropPayload(payload);
                } catch {
                    /* ignore invalid drag payload */
                }
            }}
            className={cn(
                "border-2 border-transparent rounded-xl transition-colors",
                over && "border-sidebar-primary bg-sidebar-primary/10",
                className,
            )}
        >
            {children}
        </div>
    );
};

const OccupancyView = ({ viewSwitch }) => {
    const { fetch, create } = useApiMethods();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [onDate, setOnDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [designationId, setDesignationId] = useState(undefined);
    const [searchQuery, setSearchQuery] = useState("");
    const [branchKey, setBranchKey] = useState(() => getListBranchScopeKey());
    const [expanded, setExpanded] = useState({});
    const [selected, setSelected] = useState({ listKey: null, ids: [], anchor: null });
    const [alloc, setAlloc] = useState(null);
    const [data, setData] = useState({
        blocks: [],
        unassigned: [],
        designations: [],
        assigned_count: 0,
        unassigned_count: 0,
        total_employees: 0,
        present_count: 0,
        planned_count: 0,
        short_floor_count: 0,
        can_move: false,
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const filters = { date: onDate };
            if (designationId) filters.designation = designationId;
            const response = await fetch(EMPLOYEE_OCCUPANCY, filters);
            if (response?.status === "success") {
                const payload = response.data || {};
                setData({
                    blocks: payload.blocks || [],
                    unassigned: payload.unassigned || [],
                    designations: payload.designations || [],
                    assigned_count: payload.assigned_count || 0,
                    unassigned_count: payload.unassigned_count || 0,
                    total_employees: payload.total_employees || 0,
                    present_count: payload.present_count || 0,
                    planned_count: payload.planned_count || 0,
                    short_floor_count: payload.short_floor_count || 0,
                    can_move: Boolean(payload.can_move),
                });
            } else {
                ToastMsg("error", response?.message || "Failed to load occupancy");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to load occupancy");
        } finally {
            setLoading(false);
        }
    }, [fetch, onDate, designationId]);

    useEffect(() => {
        load();
    }, [load, branchKey]);

    useEffect(() => {
        const onBranch = () => setBranchKey(getListBranchScopeKey());
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranch);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranch);
    }, []);

    const searchNeedle = searchQuery.trim().toLowerCase();
    const matchesSearch = useCallback(
        (employee) => personMatchesQuery(employee, searchNeedle),
        [searchNeedle],
    );

    useEffect(() => {
        if (!searchNeedle) return;
        const next = {};
        for (const block of data.blocks || []) {
            const floorHit = (block.floors || []).some((floor) =>
                (floor.employees || []).some(matchesSearch),
            );
            const blockHit = (block.unassigned_employees || []).some(matchesSearch);
            if (floorHit || blockHit) next[block.id] = true;
        }
        if (Object.keys(next).length) {
            setExpanded((prev) => ({ ...prev, ...next }));
        }
    }, [searchNeedle, data.blocks, matchesSearch]);

    const moveStaff = async ({ employee_ids, to_floor_id, to_block_id, make_home, unassign }) => {
        setSaving(true);
        try {
            const body = {
                employee_ids,
                date: onDate,
            };
            if (unassign) {
                body.unassign = true;
            } else {
                body.to_floor_id = to_floor_id;
                body.to_block_id = to_block_id;
                body.make_home = Boolean(make_home);
            }
            const response = await create(EMPLOYEE_OCCUPANCY, body);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Staff allocated");
                setAlloc(null);
                setSelected({ listKey: null, ids: [], anchor: null });
                await load();
            } else {
                ToastMsg("error", response?.message || "Could not allocate staff");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Could not allocate staff");
        } finally {
            setSaving(false);
        }
    };

    const listIds = (listKey) => {
        if (listKey === "unassigned") return (data.unassigned || []).map((row) => row.id);
        if (listKey?.startsWith("block-")) {
            const block = data.blocks.find((row) => `block-${row.id}` === listKey);
            return (block?.unassigned_employees || []).map((row) => row.id);
        }
        if (listKey?.startsWith("floor-")) {
            for (const block of data.blocks) {
                const floor = (block.floors || []).find((row) => `floor-${row.id}` === listKey);
                if (floor) return (floor.employees || []).map((row) => row.id);
            }
        }
        return [];
    };

    const handleSelect = (listKey, employee, event) => {
        const ordered = listIds(listKey);
        if (event.shiftKey && selected.listKey === listKey && selected.anchor != null) {
            setSelected({
                listKey,
                ids: idsBetween(ordered, selected.anchor, employee.id),
                anchor: selected.anchor,
            });
            return;
        }
        const posted = Boolean(employee.effective_floor_id || employee.work_floor_id);
        if (posted && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
            setAlloc({
                mode: "change",
                employee_ids: [employee.id],
                to_floor_id: employee.effective_floor_id || employee.work_floor_id,
                is_temp: Boolean(employee.is_temp),
                name: employee.employee_name,
            });
            return;
        }
        setSelected({ listKey, ids: [employee.id], anchor: employee.id });
    };

    const dragIdsFor = (listKey, employeeId) => {
        if (selected.listKey === listKey && selected.ids.includes(employeeId)) {
            return selected.ids;
        }
        return [employeeId];
    };

    const toggleBlock = (blockId) => {
        setExpanded((prev) => ({ ...prev, [blockId]: !prev[blockId] }));
    };

    const openAlloc = (employee_ids, floor, block) => {
        setAlloc({
            mode: "allocate",
            employee_ids,
            to_floor_id: floor?.id,
            to_block_id: !floor && block ? block.id : undefined,
            floor_name: floor?.floor_name,
            block_name: block?.block_name || floor?.block_name,
        });
    };

    const empty = !loading && data.blocks.length === 0 && data.unassigned.length === 0;

    const unassignedPanel = (
        <DropZone
            canMove={data.can_move}
            className="w-full md:w-[320px] xl:w-[360px] shrink-0 min-h-0 flex flex-col rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
            onDropPayload={(payload) => {
                moveStaff({ employee_ids: payload.employee_ids, unassign: true });
            }}
        >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-medium shrink-0">
                <Users className="w-4 h-4" />
                Unassigned ({data.unassigned.length})
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                {data.unassigned.length ? (
                    data.unassigned.filter(matchesSearch).length ? (
                        data.unassigned.filter(matchesSearch).map((employee) => (
                            <OccupancyPerson
                                key={employee.id}
                                employee={employee}
                                canMove={data.can_move}
                                selected={selected.listKey === "unassigned" && selected.ids.includes(employee.id)}
                                matched={Boolean(searchNeedle)}
                                onSelect={(row, event) => handleSelect("unassigned", row, event)}
                                onDragIds={(id) => dragIdsFor("unassigned", id)}
                            />
                        ))
                    ) : (
                        <p className="text-xs text-muted-foreground p-1">No matching employees.</p>
                    )
                ) : (
                    <p className="text-xs text-muted-foreground p-1">
                        {data.can_move
                            ? "Everyone is assigned to a floor. Drag someone off a floor to return them here."
                            : "Everyone is assigned to a floor or block."}
                    </p>
                )}
            </div>
        </DropZone>
    );

    const shortFloors = useMemo(() => {
        const rows = [];
        for (const block of data.blocks || []) {
            for (const floor of block.floors || []) {
                if (floor.status !== "short") continue;
                rows.push({
                    blockId: block.id,
                    blockName: block.block_name || "Block",
                    floorId: floor.id,
                    floorName: floor.floor_name || "Floor",
                    gap: floor.gap || 0,
                    roles: (floor.designation_rows || []).filter((row) => row.status === "short"),
                });
            }
        }
        return rows;
    }, [data.blocks]);

    const openShortFloor = (blockId, floorId) => {
        setExpanded((prev) => ({ ...prev, [blockId]: true }));
        window.requestAnimationFrame(() => {
            document.getElementById(`occupancy-floor-${floorId}`)?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        });
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col px-3 pb-3 gap-3">
            <div className="flex flex-wrap items-end gap-3 shrink-0">
                {viewSwitch ? (
                    <div className="space-y-1">
                        <span className="text-xs text-muted-foreground invisible">View</span>
                        {viewSwitch}
                    </div>
                ) : null}
                <div className="space-y-1"> 
                    <DatePicker
                        value={onDate ? dayjs(onDate) : null}
                        onChange={(date) => {
                            if (date && date.isValid()) setOnDate(date.format("YYYY-MM-DD"));
                        }}
                        format="DD-MM-YYYY"
                        allowClear={false}
                        inputReadOnly
                        className={cn(toolbarPickerClass(), "!w-[168px]")}
                        getPopupContainer={() => document.body}
                        placement="bottomLeft"
                    />
                </div>
                <div className="space-y-1 min-w-[200px]">
                    <Select
                        {...masterFormSelectProps}
                        allowClear
                        placeholder="All designations"
                        className={toolbarSelectClass()}
                        value={designationId}
                        onChange={(value) => setDesignationId(value)}
                        options={(data.designations || []).map((row) => ({
                            value: row.id,
                            label: row.designation_name,
                        }))}
                    />
                </div>
                <div className="relative min-w-[220px] max-w-xs flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search employee"
                        className={toolbarInputClass("pl-9")}
                    />
                </div>
                {data.can_move ? (
                    <p className="text-xs text-muted-foreground pb-2 max-w-xl">
                        Drag onto a block to open it, then drop on the floor. Click a posted person to change Temp vs Home.
                    </p>
                ) : null}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
                <Stat label="Active employees" value={data.total_employees} />
                <Stat label="Planned on floors" value={data.planned_count} />
                <Stat label="Present today" value={data.present_count} />
                <ShortFloorsStat
                    count={data.short_floor_count}
                    floors={shortFloors}
                    onOpenFloor={openShortFloor}
                />
            </div>

            {loading ? (
                <div className="flex-1 grid place-items-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                </div>
            ) : null}

            {empty ? (
                <div className="text-center py-16 text-muted-foreground shrink-0">
                    <Building className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>No blocks or floors to show for this branch.</p>
                    <p className="text-sm mt-1">Add them in Master → Block and Floor, then assign employees on the profile.</p>
                </div>
            ) : null}

            {!loading ? (
                <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3">
                    <div className="flex-1 min-w-0 min-h-0 overflow-y-auto space-y-3">
                        {data.blocks.map((block) => {
                            const open = Boolean(expanded[block.id]);
                            const hasFloors = (block.floors || []).length > 0;
                            return (
                                <div
                                    key={block.id}
                                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                                >
                                    <DropZone
                                        canMove={data.can_move}
                                        className={open ? "rounded-t-xl" : "rounded-xl"}
                                        onDragEnterZone={() => {
                                            if (!open) {
                                                setExpanded((prev) => ({ ...prev, [block.id]: true }));
                                            }
                                        }}
                                        onDropPayload={(payload) => {
                                            if (!hasFloors) {
                                                openAlloc(payload.employee_ids, null, block);
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleBlock(block.id)}
                                            className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-sidebar-primary/5 text-left"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                <Building className="w-4 h-4 text-sidebar-primary shrink-0" />
                                                <h3 className="font-semibold truncate">{block.block_name || "Unnamed block"}</h3>
                                            </div>
                                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-sidebar-primary/10 text-sidebar-primary">
                                                {block.planned_count ?? block.employee_count} planned
                                                {block.short_floor_count ? ` · ${block.short_floor_count} short` : ""}
                                            </span>
                                        </button>
                                        {!open ? (
                                            <p className="px-4 py-2 text-xs text-muted-foreground">
                                                Drag here to open floors, then drop on the floor you need.
                                            </p>
                                        ) : null}
                                    </DropZone>

                                    {open ? (
                                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {(block.floors || []).map((floor) => (
                                                <DropZone
                                                    key={floor.id}
                                                    canMove={data.can_move}
                                                    className="mx-2 my-1.5"
                                                    onDropPayload={(payload) => openAlloc(payload.employee_ids, { ...floor, block_name: block.block_name }, block)}
                                                >
                                                    <div id={`occupancy-floor-${floor.id}`} className="p-4 space-y-3">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2 text-sm font-medium">
                                                                <Layers className="w-4 h-4 text-muted-foreground" />
                                                                {floor.floor_name || "Unnamed floor"}
                                                            </div>
                                                            <span className={cn(
                                                                "text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full",
                                                                floorStatusClass[floor.status] || floorStatusClass.unset,
                                                            )}>
                                                                {floor.status === "short" ? `Short ${floor.gap || ""}` : floor.status || "unset"}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            Present {floor.present_count} · Planned {floor.planned_count}
                                                        </p>
                                                        {(floor.designation_rows || []).length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {floor.designation_rows.map((row) => (
                                                                    <span
                                                                        key={`${floor.id}-${row.designation_id}`}
                                                                        className={cn(
                                                                            "text-[11px] px-2 py-0.5 rounded-full border",
                                                                            row.status === "short"
                                                                                ? "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
                                                                                : "border-gray-200 dark:border-gray-700 text-muted-foreground",
                                                                        )}
                                                                    >
                                                                        {row.designation_name}: {row.present_count}/{row.min_count ?? "–"}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                        {(floor.employees || []).length ? (
                                                            (floor.employees || []).filter(matchesSearch).length ? (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                {(floor.employees || []).filter(matchesSearch).map((employee) => (
                                                                    <OccupancyPerson
                                                                        key={employee.id}
                                                                        employee={employee}
                                                                        canMove={data.can_move}
                                                                        selected={selected.listKey === `floor-${floor.id}` && selected.ids.includes(employee.id)}
                                                                        matched={Boolean(searchNeedle)}
                                                                        onSelect={(row, event) => handleSelect(`floor-${floor.id}`, row, event)}
                                                                        onDragIds={(id) => dragIdsFor(`floor-${floor.id}`, id)}
                                                                        onChangePosting={(row) => handleSelect(`floor-${floor.id}`, row, { shiftKey: false })}
                                                                    />
                                                                ))}
                                                            </div>
                                                            ) : (
                                                                <p className="text-xs text-muted-foreground">No matching employees on this floor.</p>
                                                            )
                                                        ) : (
                                                            <p className="text-xs text-muted-foreground">
                                                                {data.can_move ? "Drop employees here." : "No employees assigned to this floor."}
                                                            </p>
                                                        )}
                                                    </div>
                                                </DropZone>
                                            ))}

                                            {(block.unassigned_employees || []).filter(matchesSearch).length > 0 && (
                                                <div className="p-4 space-y-3">
                                                    <div className="flex items-center gap-2 text-sm font-medium">
                                                        <Users className="w-4 h-4 text-muted-foreground" />
                                                        Assigned to block, no floor
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {(block.unassigned_employees || []).filter(matchesSearch).map((employee) => (
                                                            <OccupancyPerson
                                                                key={employee.id}
                                                                employee={employee}
                                                                canMove={data.can_move}
                                                                selected={selected.listKey === `block-${block.id}` && selected.ids.includes(employee.id)}
                                                                matched={Boolean(searchNeedle)}
                                                                onSelect={(row, event) => handleSelect(`block-${block.id}`, row, event)}
                                                                onDragIds={(id) => dragIdsFor(`block-${block.id}`, id)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {!block.floors?.length ? (
                                                <p className="p-4 text-sm text-muted-foreground">
                                                    This block has no floors. Drop here to assign the block as home, or add floors in Master.
                                                </p>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    {unassignedPanel}
                </div>
            ) : null}

            <DialogModal
                open={Boolean(alloc)}
                onClose={() => !saving && setAlloc(null)}
                title={alloc?.mode === "change" ? "Change posting" : "Allocate to floor"}
                hideSaveButton
                showActions={false}
                width="420px"
            >
                <p className="text-sm text-muted-foreground mb-4">
                    {alloc?.name ? `${alloc.name} · ` : ""}
                    {alloc?.block_name ? `${alloc.block_name} · ` : ""}
                    {alloc?.floor_name || (alloc?.to_block_id ? "Block only" : "Selected floor")}
                    {alloc?.mode === "change" ? (alloc.is_temp ? " · currently Temp" : " · currently Home") : ""}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        variant="outline"
                        className="flex-1"
                        disabled={saving}
                        onClick={() => moveStaff({ ...alloc, make_home: false })}
                    >
                        Temporary (today only)
                    </Button>
                    <Button
                        className="flex-1"
                        disabled={saving}
                        onClick={() => moveStaff({ ...alloc, make_home: true })}
                    >
                        Permanent (home floor)
                    </Button>
                </div>
            </DialogModal>
        </div>
    );
};

const Stat = ({ label, value, warn }) => (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn("text-2xl font-semibold mt-1", warn && "text-red-600 dark:text-red-400")}>{value}</p>
    </div>
);

const ShortFloorsStat = ({ count, floors, onOpenFloor }) => {
    const [open, setOpen] = useState(false);
    const hasShort = count > 0;

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => hasShort && setOpen((prev) => !prev)}
                className={cn(
                    "w-full rounded-xl border bg-white dark:bg-gray-800 p-4 text-left",
                    hasShort
                        ? "border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-50/60 dark:hover:bg-red-900/20"
                        : "border-gray-200 dark:border-gray-700 cursor-default",
                )}
            >
                <p className="text-xs text-muted-foreground">Floors short</p>
                <p className={cn("text-2xl font-semibold mt-1", hasShort && "text-red-600 dark:text-red-400")}>
                    {count}
                </p>
                {hasShort ? (
                    <p className="text-[11px] text-red-600/80 dark:text-red-400 mt-1">Click to view list</p>
                ) : null}
            </button>
            {open && hasShort ? (
                <div className="absolute right-0 top-full z-30 mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 shadow-lg p-2 max-h-72 overflow-y-auto">
                    <p className="px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300">Short floors</p>
                    {floors.map((floor) => (
                        <button
                            key={`${floor.blockId}-${floor.floorId}`}
                            type="button"
                            onClick={() => {
                                onOpenFloor(floor.blockId, floor.floorId);
                                setOpen(false);
                            }}
                            className="w-full text-left rounded-lg px-2 py-2 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                            <p className="text-sm font-medium">
                                {floor.blockName} · {floor.floorName}
                            </p>
                            <p className="text-xs text-red-600 dark:text-red-400">
                                {floor.roles.length
                                    ? floor.roles.map((row) => (
                                        `${row.designation_name || "Role"} ${row.planned_count}/${row.min_count}`
                                    )).join(" · ")
                                    : `Short by ${floor.gap}`}
                            </p>
                        </button>
                    ))}
                    {!floors.length ? (
                        <p className="px-2 py-2 text-xs text-muted-foreground">No short floors on this date.</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default OccupancyView;
